import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_LOGIN_URL, AuthService, AuthenticationFacade } from '@navfleet/client/authentication/domain/data-access';
import { UserLoginCommand } from '@navfleet/client/authentication/domain/models';
import {
    ErrorDetails,
    ErrorWithTranslationCode,
    ValidationProblemDetails,
    getErrorMessages,
} from '@navfleet/client/shared/models/common';
import { ToasterService } from '@navfleet/client/shared/services/toast';
import { logDebug, logError } from '@navfleet/client/shared/util/log';
import { environment } from '@navfleet/shared/core/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, concatMap, filter, finalize, take } from 'rxjs/operators';

const SKIP_INTERCEPTION_CATEGORIES = ['blocked-by-related-entity'];

/**
 * APIs other than NavFleet's that authenticate with NavFleet's login cookie. Their 401 means the cookie's token has
 * expired: with "keep me logged in" the NavFleet session is refreshed once and the request retried, instead of logging
 * the user out. A failed refresh logs the user out (the session is over); a 401 on the retry after a successful refresh
 * is that API's own problem, so it is reported as an error and the (valid) NavFleet session is kept.
 */
const REFRESH_SESSION_ON_UNAUTHORIZED_APIS = [environment.PERFORMANCE_API, environment.LOGBOOK_ANALYTICS_API];

const isRefreshSessionOnUnauthorizedUrl = (url: string | null): boolean =>
    !!url && REFRESH_SESSION_ON_UNAUTHORIZED_APIS.some((api) => url.startsWith(api));

const skipInterception = (errorResponse: HttpErrorResponse): boolean =>
    errorResponse.error &&
    errorResponse.error.errorCategory &&
    SKIP_INTERCEPTION_CATEGORIES.some((v) => v === errorResponse.error.errorCategory);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    private readonly authenticationFacade = inject(AuthenticationFacade);
    private readonly authService = inject(AuthService);
    private readonly toasterService = inject(ToasterService);
    private readonly isRenewingToken$ = new BehaviorSubject<boolean>(false);

    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Set once the request is retried after a session refresh; its 401 then does not log the user out.
        let isRetryAfterRefresh = false;
        const retry = (): Observable<HttpEvent<any>> => {
            isRetryAfterRefresh = true;
            return next.handle(request);
        };

        return next.handle(request).pipe(
            // Handle token refresh for the APIs that use NavFleet's login cookie
            catchError((errorResponse: HttpErrorResponse) => {
                const keepMeLoggedIn = !!JSON.parse(localStorage.getItem('keepMeLoggedIn')!);
                if (
                    keepMeLoggedIn &&
                    errorResponse.status === 401 &&
                    isRefreshSessionOnUnauthorizedUrl(errorResponse.url)
                ) {
                    if (!this.isRenewingToken$.getValue()) {
                        this.isRenewingToken$.next(true);
                        return this.refreshToken().pipe(
                            // Once the refresh is over (also when it failed), the requests that waited for it retry.
                            finalize(() => this.isRenewingToken$.next(false)),
                            concatMap(retry)
                        );
                    }

                    return this.isRenewingToken$.pipe(
                        filter((isRenewing) => !isRenewing),
                        take(1),
                        concatMap(retry)
                    );
                }
                throw errorResponse;
            }),
            // Handle errors
            catchError((errorResponse: HttpErrorResponse) => {
                // If the request falls into categories that should skip interception, then throw the error back
                if (skipInterception(errorResponse)) {
                    throw errorResponse;
                }

                // If the error comes from a Blob response, parse and rethrow
                if (
                    errorResponse instanceof HttpErrorResponse &&
                    errorResponse.error instanceof Blob &&
                    errorResponse.error.type === 'application/json'
                ) {
                    // https://github.com/angular/issues/19888
                    // When request of type Blob, the error is also in Blob instead of object of the json data
                    return new Promise<any>((_, reject) => {
                        let reader = new FileReader();
                        reader.onload = (e: Event): void => {
                            try {
                                const errmsg = JSON.parse((<any>e.target).result);
                                reject(
                                    new HttpErrorResponse({
                                        error: errmsg,
                                        headers: errorResponse.headers,
                                        status: errorResponse.status,
                                        statusText: errorResponse.statusText,
                                        url: errorResponse.url ?? undefined,
                                    })
                                );
                            } catch {
                                reject(errorResponse);
                            }
                        };
                        reader.onerror = (): void => {
                            reject(errorResponse);
                        };
                        reader.readAsText(errorResponse.error);
                    });
                }

                let { error, status, url } = errorResponse;
                logDebug('Http Error Interceptor:', status, request);

                if (!this.shouldIgnoreError(request, errorResponse)) {
                    if (status === 401 && !isRetryAfterRefresh) {
                        if (error?.translationCode) {
                            this.toasterService.error(error.translationCode);
                        }
                        if (!error) {
                            error = {
                                message: 'Unauthorized',
                                statusCode: status,
                                translationCode: 'MESSAGE.UNAUTHORIZED',
                            };
                        }
                        this.authenticationFacade.logout(true);
                    } else if (this.isValidError(status)) {
                        if (!error) {
                            error = {
                                message: errorResponse.statusText,
                                statusCode: status,
                                translationCode: '',
                            };
                        }

                        if (!error?.statusCode && status) {
                            error = {
                                ...error,
                                statusCode: status,
                            };
                        }

                        this.triggerToastMessage(error);
                        const message = `Http Status Code:${error?.statusCode}: Error: ${error?.message} URL: ${url}`;
                        logError('Http Error Interceptor:', message);
                    }
                }

                // throw error back to calling component to handle for special cases
                return throwError(() => new ErrorWithTranslationCode(error));
            })
        );
    }

    /**
     * Main composing toast method
     * Takes constructed toast message and sends it to service
     * @param error
     */
    private triggerToastMessage(errorResponse: ErrorDetails | ValidationProblemDetails): void {
        const errors: ErrorDetails[] =
            getErrorMessages(errorResponse as ValidationProblemDetails) || ([errorResponse] as ErrorDetails[]);

        for (const error of errors) {
            const toastMessage = this.buildToastMessage(error);
            const toastTitle = this.buildToastTitle(error);
            this.toasterService.error(toastMessage, toastTitle);
        }
    }

    /**
     * Takes error url and formats the URI along with status code
     * @param error
     * @param url
     */
    private buildToastTitle(error: ErrorDetails | any): string | undefined {
        if ('title' in error) {
            return error.title;
        }
        return undefined;
    }

    /**
     * Builds toast message from error response
     * @param error
     */
    private buildToastMessage(error: ErrorDetails): string {
        const toastMessage = error?.translationCode?.length ? error.translationCode : error.message;
        return toastMessage;
    }

    private refreshToken(): Observable<void> {
        return this.authService.refreshToken();
    }

    /**
     * Checks if the
     * @param request The HttpRequest
     * @param errorResponse The HttpErrorResponse from the request
     * @returns True if the error response should be ignored
     */
    private shouldIgnoreError(request: HttpRequest<any>, errorResponse: HttpErrorResponse): boolean {
        return this.wasAutoLoginRequest(request, errorResponse);
    }

    /**
     * Returns true if status is valid for the interceptor to handle
     * @param status
     */
    private isValidError(status: number): boolean {
        // Success statuses
        if (status >= 200 && status <= 299) {
            return false;
        }

        return true;
    }

    /**
     * Checks if the request was a auto login request.
     * @param request The HttpRequest
     * @returns True if the request was a auto login request
     */
    private wasAutoLoginRequest(request: HttpRequest<UserLoginCommand>, errorResponse: HttpErrorResponse): boolean {
        return (
            request.url === API_LOGIN_URL &&
            errorResponse.status === 401 &&
            ((request?.body?.userName === '' && request?.body?.password === '') || !!request?.body?.tenantId)
        );
    }
}
